import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Card,
  CardContent,
  Grid,
  Snackbar
} from '@mui/material';
import {
  Download as DownloadIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  CloudDownload as CloudDownloadIcon,
  InsertDriveFile as FileIcon,
  Schedule as ScheduleIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';

import { sowService } from '../services/sowService';

const DocumentManager = ({ onDocumentSelect }) => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [metadataDialog, setMetadataDialog] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState({});
  const [filters, setFilters] = useState({
    customerName: '',
    status: '',
    maxDocuments: 50
  });
  const [notification, setNotification] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    loadDocuments();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const loadDocuments = async (activeFilters = filters) => {
    setLoading(true);
    setError(null);
    
    try {
      const response = await sowService.listDocuments(activeFilters);
      setDocuments(response.documents || []);
    } catch (err) {
      setError(`Failed to load documents: ${err.message}`);
      setDocuments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async (document, downloadType = 'direct') => {
    const documentId = document.document_id || document.job_id;
    
    try {
      setDownloadProgress(prev => ({ ...prev, [documentId]: { percentage: 0, active: true } }));
      
      // If document has a download_url (presigned URL), use it directly
      if (document.download_url) {
        console.log('📥 Using presigned URL for download');
        
        const result = await sowService.downloadFromPresignedUrl(
          document.download_url, 
          document.filename || `SOW_${document.customer_name}.docx`
        );
        
        setNotification({
          open: true,
          message: `Document "${result.filename}" downloaded successfully!`,
          severity: 'success'
        });
      } else {
        // Fallback: Get fresh status with presigned URL
        console.log('📥 Fetching fresh download URL');
        const statusResponse = await sowService.getJobStatus(documentId);
        
        if (statusResponse.download_url) {
          const result = await sowService.downloadFromPresignedUrl(
            statusResponse.download_url,
            statusResponse.filename || `SOW_${document.customer_name}.docx`
          );
          
          setNotification({
            open: true,
            message: `Document "${result.filename}" downloaded successfully!`,
            severity: 'success'
          });
        } else {
          throw new Error('Download URL not available. Document may not be ready yet.');
        }
      }

    } catch (err) {
      console.error('Download error:', err);
      setNotification({
        open: true,
        message: `Download failed: ${err.message}`,
        severity: 'error'
      });
    } finally {
      setDownloadProgress(prev => ({ ...prev, [documentId]: { active: false } }));
    }
  };

  const handleViewMetadata = async (document) => {
    try {
      const documentId = document.document_id || document.job_id;
      
      // Get fresh job status with all details
      const response = await sowService.getJobStatus(documentId);
      
      setSelectedDocument({
        ...document,
        ...response,
        metadata: {
          storage_location: document.storage_location || 'S3',
          file_type: 'Microsoft Word Document (.docx)',
          extracted_info: {
            generation_date: document.created_at,
            generation_time: document.generation_time || 'N/A'
          },
          download_endpoints: {
            presigned_url: response.download_url
          }
        }
      });
      setMetadataDialog(true);
    } catch (err) {
      setNotification({
        open: true,
        message: `Failed to load document metadata: ${err.message}`,
        severity: 'error'
      });
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    loadDocuments(filters);
  };

  const clearFilters = () => {
    const reset = { customerName: '', status: '', maxDocuments: 50 };
    setFilters(reset);
    loadDocuments(reset);
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Unknown';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const formatDate = (dateString) => {
    try {
      return format(parseISO(dateString), 'MMM dd, yyyy HH:mm');
    } catch {
      return dateString;
    }
  };

  const getDocumentStatus = (document) => {
    const status = document.status?.toLowerCase();
    
    switch (status) {
      case 'completed':
        return { label: 'Completed', color: 'success' };
      case 'processing':
        return { label: 'Processing', color: 'info' };
      case 'queued':
        return { label: 'Queued', color: 'warning' };
      case 'failed':
        return { label: 'Failed', color: 'error' };
      case 'retrying':
        return { label: 'Retrying', color: 'warning' };
      default:
        return { label: status || 'Unknown', color: 'default' };
    }
  };

  if (loading && documents.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress />
        <Typography variant="body1" sx={{ ml: 2 }}>
          Loading documents...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', background: 'transparent', position: 'relative' }}>
      {/* BCE Background Blobs */}
      <div className="eclipse" style={{ width: '556px', height: '556px', top: '100px', left: '40px' }} />
      <div className="planet" style={{ width: '524px', height: '524px', top: '300px', right: '40px' }} />
      <div className="planet1" style={{ width: '450px', height: '450px', bottom: '100px', left: 'calc(50% - 225px)' }} />
      
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 4 }, pb: 6, pt: 4 }}>

      {/* Page Header Card */}
      <Box
        sx={{ 
          textAlign: 'center', 
          mb: 4,
          py: { xs: 4, md: 6 },
          px: 3,
          background: 'rgba(255, 255, 255, 0.25)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          border: '1px solid rgba(255, 255, 255, 0.3)',
          borderRadius: '16px',
          position: 'relative',
          overflow: 'hidden',
          boxShadow: '0 8px 32px 0 rgba(89, 92, 234, 0.15)',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `
              linear-gradient(135deg, rgba(89, 92, 234, 0.1) 0%, rgba(103, 138, 231, 0.05) 50%, rgba(79, 193, 246, 0.1) 100%)
            `,
            pointerEvents: 'none',
            zIndex: 0,
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            top: '-50%',
            left: '-50%',
            width: '200%',
            height: '200%',
            background: `
              radial-gradient(circle at 30% 50%, rgba(89, 92, 234, 0.08) 0%, transparent 50%),
              radial-gradient(circle at 70% 50%, rgba(79, 193, 246, 0.08) 0%, transparent 50%)
            `,
            pointerEvents: 'none',
            zIndex: 0,
          },
        }}
      >
        <Chip
          label="Document Archive"
          size="small"
          sx={{
            mb: 2,
            background: 'rgba(89, 92, 234, 0.1)',
            color: '#595cea',
            fontFamily: 'DM Sans, sans-serif',
            fontWeight: 600,
            fontSize: '0.75rem',
            border: '1px solid rgba(89, 92, 234, 0.2)',
            position: 'relative',
            zIndex: 1,
          }}
        />
        <Typography
          variant="h4"
          sx={{ 
            fontFamily: 'Poppins, sans-serif',
            color: '#1e1e1e', 
            fontWeight: 600, 
            mb: 1, 
            letterSpacing: '-0.02em',
            position: 'relative',
            zIndex: 1,
          }}
        >
          Generated Documents
        </Typography>
        <Typography 
          variant="body1" 
          sx={{ 
            fontFamily: 'DM Sans, sans-serif',
            color: '#1e1e1e', 
            maxWidth: 600,
            mx: 'auto',
            position: 'relative',
            zIndex: 1,
          }}
        >
          Browse, search, and download all previously generated SOW documents.
        </Typography>
      </Box>

      {/* Filters */}
      <Paper
        elevation={0}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: '16px',
          border: '1px solid rgba(89, 92, 234, 0.15)',
          background: '#ffffff',
          boxShadow: '0 2px 8px rgba(89, 92, 234, 0.08)',
          transition: 'all 0.3s ease',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(89, 92, 234, 0.12)',
          },
        }}
      >
        <Typography 
          variant="h6" 
          gutterBottom 
          sx={{ 
            fontFamily: 'Poppins, sans-serif',
            fontWeight: 600, 
            color: '#1e1e1e', 
            mb: 3,
            letterSpacing: '-0.01em',
          }}
        >
          Filter Documents
        </Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              label="Customer Name"
              value={filters.customerName}
              onChange={(e) => handleFilterChange('customerName', e.target.value)}
              fullWidth
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: 'DM Sans, sans-serif',
                  '&:hover fieldset': {
                    borderColor: '#595cea',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#595cea',
                  },
                },
                '& .MuiInputLabel-root': {
                  fontFamily: 'DM Sans, sans-serif',
                  '&.Mui-focused': {
                    color: '#595cea',
                  },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel
                sx={{
                  fontFamily: 'DM Sans, sans-serif',
                  '&.Mui-focused': {
                    color: '#595cea',
                  },
                }}
              >
                Status
              </InputLabel>
              <Select
                value={filters.status}
                label="Status"
                onChange={(e) => handleFilterChange('status', e.target.value)}
                sx={{
                  fontFamily: 'DM Sans, sans-serif',
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#595cea',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#595cea',
                  },
                }}
              >
                <MenuItem value="" sx={{ fontFamily: 'DM Sans, sans-serif' }}>All Statuses</MenuItem>
                <MenuItem value="completed" sx={{ fontFamily: 'DM Sans, sans-serif' }}>Completed</MenuItem>
                <MenuItem value="processing" sx={{ fontFamily: 'DM Sans, sans-serif' }}>Processing</MenuItem>
                <MenuItem value="queued" sx={{ fontFamily: 'DM Sans, sans-serif' }}>Queued</MenuItem>
                <MenuItem value="failed" sx={{ fontFamily: 'DM Sans, sans-serif' }}>Failed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <TextField
              label="Max Results"
              type="number"
              value={filters.maxDocuments}
              onChange={(e) => handleFilterChange('maxDocuments', parseInt(e.target.value) || 50)}
              fullWidth
              size="small"
              inputProps={{ min: 1, max: 200 }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontFamily: 'DM Sans, sans-serif',
                  '&:hover fieldset': {
                    borderColor: '#595cea',
                  },
                  '&.Mui-focused fieldset': {
                    borderColor: '#595cea',
                  },
                },
                '& .MuiInputLabel-root': {
                  fontFamily: 'DM Sans, sans-serif',
                  '&.Mui-focused': {
                    color: '#595cea',
                  },
                },
              }}
            />
          </Grid>
          <Grid item xs={12} md={5}>
            <Box display="flex" gap={1}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={applyFilters}
                sx={{
                  background: 'linear-gradient(135deg, #595cea 0%, #678ae7 100%)',
                  boxShadow: '0 2px 8px rgba(89, 92, 234, 0.25)',
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontFamily: 'DM Sans, sans-serif',
                  fontWeight: 600,
                  px: 3,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4a4dd6 0%, #595cea 100%)',
                    boxShadow: '0 4px 12px rgba(89, 92, 234, 0.35)',
                    transform: 'translateY(-1px)',
                  },
                }}
              >
                Apply Filters
              </Button>
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={clearFilters}
                sx={{ 
                  borderRadius: '8px', 
                  textTransform: 'none', 
                  fontFamily: 'DM Sans, sans-serif',
                  fontWeight: 600, 
                  borderColor: 'rgba(89, 92, 234, 0.3)', 
                  color: '#595cea',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    borderColor: '#595cea',
                    background: 'rgba(89, 92, 234, 0.05)',
                  },
                }}
              >
                Clear
              </Button>
              <IconButton 
                onClick={loadDocuments} 
                sx={{ 
                  color: '#595cea',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    background: 'rgba(89, 92, 234, 0.1)',
                    transform: 'rotate(180deg)',
                  },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Documents List */}
      {documents.length === 0 ? (
        <Paper
          elevation={0}
          sx={{
            p: 6,
            textAlign: 'center',
            borderRadius: '16px',
            border: '1px solid rgba(89, 92, 234, 0.15)',
            background: 'rgba(89, 92, 234, 0.02)',
          }}
        >
          <FileIcon sx={{ fontSize: 56, color: 'rgba(89, 92, 234, 0.3)', mb: 2 }} />
          <Typography 
            variant="h6" 
            sx={{ 
              fontFamily: 'Poppins, sans-serif',
              color: '#1e1e1e', 
              fontWeight: 600, 
              mb: 1 
            }}
          >
            No documents found
          </Typography>
          <Typography 
            variant="body2" 
            sx={{ 
              fontFamily: 'DM Sans, sans-serif',
              color: 'rgba(30, 30, 30, 0.7)' 
            }}
          >
            {filters.customerName ? 'Try adjusting your filters or generate' : 'Generate'} your first SOW to see it here.
          </Typography>
        </Paper>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ 
            borderRadius: '16px', 
            border: '1px solid rgba(89, 92, 234, 0.15)', 
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(89, 92, 234, 0.08)',
          }}
        >
          <Table>
            <TableHead>
              <TableRow sx={{ background: 'linear-gradient(135deg, rgba(89, 92, 234, 0.05) 0%, rgba(79, 193, 246, 0.05) 100%)' }}>
                <TableCell sx={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 700, color: '#595cea', fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Document</TableCell>
                <TableCell sx={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 700, color: '#595cea', fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Customer</TableCell>
                <TableCell sx={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 700, color: '#595cea', fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Type</TableCell>
                <TableCell sx={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 700, color: '#595cea', fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Created</TableCell>
                <TableCell sx={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 700, color: '#595cea', fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Size</TableCell>
                <TableCell sx={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 700, color: '#595cea', fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</TableCell>
                <TableCell align="center" sx={{ fontFamily: 'DM Sans, sans-serif', fontWeight: 700, color: '#595cea', fontSize: '0.8125rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {documents.map((document, index) => {
                const documentId = document.document_id || document.s3_key || `doc-${index}`;
                const progress = downloadProgress[documentId];
                const status = getDocumentStatus(document);
                
                return (
                  <TableRow 
                    key={documentId} 
                    hover
                    sx={{
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: 'rgba(89, 92, 234, 0.03)',
                      },
                    }}
                  >
                    <TableCell sx={{ maxWidth: 250 }}>
                      <Box display="flex" alignItems="center">
                        <FileIcon sx={{ mr: 1, color: '#595cea', flexShrink: 0 }} />
                        <Box sx={{ overflow: 'hidden' }}>
                          <Tooltip title={document.filename || document.document_id || 'Unknown Document'} arrow>
                            <Typography 
                              variant="body2" 
                              fontWeight="medium"
                              sx={{ 
                                fontFamily: 'DM Sans, sans-serif', 
                                color: '#1e1e1e',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                cursor: 'default',
                              }}
                            >
                              {document.filename || document.document_id || 'Unknown Document'}
                            </Typography>
                          </Tooltip>
                          {document.project_type && (
                            <Typography 
                              variant="caption" 
                              sx={{ 
                                fontFamily: 'DM Sans, sans-serif', 
                                color: 'rgba(30, 30, 30, 0.5)',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                display: 'block',
                              }}
                            >
                              {document.project_type}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <BusinessIcon sx={{ mr: 1, fontSize: 16, color: 'rgba(89, 92, 234, 0.6)' }} />
                        <Typography 
                          variant="body2"
                          sx={{ fontFamily: 'DM Sans, sans-serif', color: '#1e1e1e' }}
                        >
                          {document.customer_name || 'Unknown Customer'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={document.document_type || 'SOW'} 
                        size="small" 
                        sx={{
                          fontFamily: 'DM Sans, sans-serif',
                          fontWeight: 600,
                          background: 'rgba(89, 92, 234, 0.1)',
                          color: '#595cea',
                          border: '1px solid rgba(89, 92, 234, 0.2)',
                        }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <ScheduleIcon sx={{ mr: 1, fontSize: 16, color: 'rgba(89, 92, 234, 0.6)' }} />
                        <Typography 
                          variant="body2"
                          sx={{ fontFamily: 'DM Sans, sans-serif', color: '#1e1e1e' }}
                        >
                          {document.created_at ? formatDate(document.created_at) : 'Unknown'}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography 
                        variant="body2"
                        sx={{ fontFamily: 'DM Sans, sans-serif', color: '#1e1e1e' }}
                      >
                        {formatFileSize(document.file_size)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={status.label} 
                        size="small" 
                        sx={{
                          fontFamily: 'DM Sans, sans-serif',
                          fontWeight: 600,
                          ...(status.color === 'success' && {
                            background: '#b4e4ce',
                            color: '#1e1e1e',
                            border: 'none',
                          }),
                          ...(status.color === 'info' && {
                            background: '#b8d9f5',
                            color: '#1e1e1e',
                            border: 'none',
                          }),
                          ...(status.color === 'warning' && {
                            background: '#f5dda8',
                            color: '#1e1e1e',
                            border: 'none',
                          }),
                          ...(status.color === 'error' && {
                            background: '#f5b8c5',
                            color: '#1e1e1e',
                            border: 'none',
                          }),
                        }}
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Box display="flex" justifyContent="center" gap={1}>
                        {document.status === 'completed' && document.download_url ? (
                          <Tooltip title="Download Document">
                            <IconButton
                              size="small"
                              onClick={() => handleDownload(document, 'direct')}
                              disabled={progress?.active}
                              sx={{
                                color: '#595cea',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  background: 'rgba(89, 92, 234, 0.1)',
                                  transform: 'scale(1.1)',
                                },
                              }}
                            >
                              {progress?.active ? (
                                <CircularProgress size={16} sx={{ color: '#595cea' }} />
                              ) : (
                                <DownloadIcon />
                              )}
                            </IconButton>
                          </Tooltip>
                        ) : document.status === 'completed' ? (
                          <Tooltip title="Refresh to get download link">
                            <IconButton
                              size="small"
                              onClick={() => loadDocuments()}
                              sx={{
                                color: '#4fc1f6',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  background: 'rgba(79, 193, 246, 0.1)',
                                  transform: 'rotate(180deg)',
                                },
                              }}
                            >
                              <RefreshIcon />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title={`Status: ${document.status}`}>
                            <IconButton size="small" disabled>
                              <ScheduleIcon />
                            </IconButton>
                          </Tooltip>
                        )}
                        
                        <Tooltip title="View Details">
                          <IconButton
                            size="small"
                            onClick={() => handleViewMetadata(document)}
                            sx={{
                              color: '#595cea',
                              transition: 'all 0.3s ease',
                              '&:hover': {
                                background: 'rgba(89, 92, 234, 0.1)',
                                transform: 'scale(1.1)',
                              },
                            }}
                          >
                            <InfoIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      
                      {progress?.active && (
                        <Box sx={{ mt: 1 }}>
                          <LinearProgress 
                            variant="determinate" 
                            value={progress.percentage || 0}
                            sx={{ height: 4, borderRadius: 2 }}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {progress.percentage || 0}%
                          </Typography>
                        </Box>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      </Box> {/* close maxWidth wrapper */}

      {/* Document Metadata Dialog */}
      <Dialog 
        open={metadataDialog} 
        onClose={() => setMetadataDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '20px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 25px 50px -12px rgba(0,0,0,0.15)',
          }
        }}
      >
        <DialogTitle sx={{ fontWeight: 700, color: '#111827', borderBottom: '1px solid #f3f4f6', pb: 2 }}>
          Document Details
        </DialogTitle>
        <DialogContent>
          {selectedDocument && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ color: '#2563eb', fontWeight: 700 }}>
                        Basic Information
                      </Typography>
                      <Box sx={{ '& > div': { mb: 1 } }}>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">Document ID:</Typography>
                          <Typography variant="body2" fontFamily="monospace">
                            {selectedDocument.document_id || 'N/A'}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">Customer:</Typography>
                          <Typography variant="body2">
                            {selectedDocument.customer_name || 'N/A'}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">Project Type:</Typography>
                          <Typography variant="body2">
                            {selectedDocument.project_type || 'N/A'}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">File Size:</Typography>
                          <Typography variant="body2">
                            {formatFileSize(selectedDocument.file_size)}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
                
                <Grid item xs={12} md={6}>
                  <Card variant="outlined" sx={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                    <CardContent>
                      <Typography variant="h6" gutterBottom sx={{ color: '#2563eb', fontWeight: 700 }}>
                        Storage Information
                      </Typography>
                      <Box sx={{ '& > div': { mb: 1 } }}>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">Storage:</Typography>
                          <Chip 
                            label={selectedDocument.metadata?.storage_location || 'Unknown'} 
                            size="small"
                            color={selectedDocument.metadata?.storage_location === 'S3' ? 'success' : 'default'}
                          />
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">Created:</Typography>
                          <Typography variant="body2">
                            {selectedDocument.created_at ? formatDate(selectedDocument.created_at) : 'N/A'}
                          </Typography>
                        </Box>
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2" color="text.secondary">File Type:</Typography>
                          <Typography variant="body2">
                            {selectedDocument.metadata?.file_type || 'Microsoft Word Document'}
                          </Typography>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                {selectedDocument.metadata?.extracted_info && (
                  <Grid item xs={12}>
                    <Card variant="outlined" sx={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: '#2563eb', fontWeight: 700 }}>
                          Extracted Information
                        </Typography>
                        <Box sx={{ '& > div': { mb: 1 } }}>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">Generation Date:</Typography>
                            <Typography variant="body2">
                              {selectedDocument.metadata.extracted_info.generation_date}
                            </Typography>
                          </Box>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="body2" color="text.secondary">Generation Time:</Typography>
                            <Typography variant="body2">
                              {selectedDocument.metadata.extracted_info.generation_time}
                            </Typography>
                          </Box>
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}

                {selectedDocument.metadata?.download_endpoints && (
                  <Grid item xs={12}>
                    <Card variant="outlined" sx={{ borderRadius: '12px', border: '1px solid #e5e7eb' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ color: '#2563eb', fontWeight: 700 }}>
                          Download Options
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<DownloadIcon />}
                            onClick={() => handleDownload(selectedDocument, 'direct')}
                          >
                            Direct Download
                          </Button>
                          {selectedDocument.s3_location && (
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<CloudDownloadIcon />}
                              onClick={() => handleDownload(selectedDocument, 's3')}
                            >
                              S3 Download
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMetadataDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Notification Snackbar */}
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={() => setNotification({ ...notification, open: false })}
      >
        <Alert 
          onClose={() => setNotification({ ...notification, open: false })} 
          severity={notification.severity}
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default DocumentManager;